import {makeStyles} from "tss-react/mui";
import {Box, BoxProps, Button} from "@mui/material";
import {forwardRef, useCallback, useContext, useEffect, useState} from "react";
import {TrainingfeedContext} from "../../contexts/trainingFeedContext";
import {DayContainer} from "../../pages/TrainingFeedPage/DayContainer";
import {OverdueModules} from "../../pages/TrainingFeedPage/OverdueModules";
import {ScheduledDayModules} from "../../pages/TrainingFeedPage/ScheduledDayModules";
import {palette} from "../../constants";
import {Announcements} from "../../pages/TrainingFeedPage/Announcements/Announcements";
import {AnnouncementContext} from "../../contexts/announcementContext";
import {useSSTranslation} from "../../i18n/useSSTranslation";

interface IProps extends BoxProps {
}

const useStyles = makeStyles()((theme) => ({
	trainingFeedSideMenuContent: {
		padding: "15px",
		position: "relative"
	},
	tapToLoadBtn: {
		width: "100%"
	},
	dayContainer: {
		position: "relative",
		"[class*='dayIconContainer'] [class*='dayIcon']": {
			background: palette.drawer
		}
	},
	tapToLoadBtnContainer: {
		width: "100%",
		bottom: "-50px",
		left: 0,
		padding: "100px 35px 35px 55px",
		boxSizing: "border-box",
		position: "absolute",
		background: "linear-gradient(180deg, rgba(44, 44, 44, 0) 0%, " + palette.drawer + " 60%)"

	},
	placeholderModule: {
		width:  "100%",
		borderRadius: "16px",
		background: palette.grey05,
		padding: "12px",
		display: "flex",
		gap: "12px",
		"& > div": {
			background: palette.grey05,
			width: "90px",
			borderRadius: "5px",
			height: "50px"
		},
		"& > div:nth-of-type(2)": {
			background: palette.grey05,
			flexGrow: 2,
			marginTop: "5px",
			borderRadius: "5px",
			height: "15px"
		}
	}
}));

const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);

const next = new Date();
next.setDate(next.getDate() + 2);

export const TrainingFeedSideMenuContent = forwardRef<HTMLDivElement, IProps>(function TrainingFeedSideMenuContent({className, ...props}, ref) {
	const {classes, cx} = useStyles();
	const {trainingfeedState: curriculumState, trainingfeedDispatch: curriculumDispatch} = useContext(TrainingfeedContext)
	const overDueModules = (typeof curriculumState?.["overdue"] === "string" ? [] : curriculumState?.["overdue"] ) ?? [];
	const {announcementState} = useContext(AnnouncementContext)
	const {t} = useSSTranslation();

	const [nextDayToLoad, setNextDayToLoad] = useState<Date>(() => {
		const e = new Date();
		e.setDate(e.getDate() + 3);
		return e;
	});

	const handleRequestDay = useCallback(() => {
		curriculumDispatch({type: "getDay", day: nextDayToLoad})
		const newDate = new Date(nextDayToLoad)
		newDate.setDate(newDate.getDate() + 1);
		setNextDayToLoad(newDate)
	}, [nextDayToLoad])

	useEffect(() => {
		if(!curriculumState?.[next.getTime()]){
			curriculumDispatch({type: "getDay", day: next})
		}
	})

	const hasAnnouncements = (announcementState?.length ?? 0) > 0;
	const hasOverdue = (overDueModules?.length ?? 0) > 0 ;
	return (
		<Box
			ref={ref}
			className={cx(classes.trainingFeedSideMenuContent, className)}
			data-test-id="training-feed-side-menu"
			role="complementary"
			aria-label="Training feed side menu"
			{...props}
		>
			{hasAnnouncements && <Announcements
				slim
				data-test-id="training-feed-side-menu-announcements"
			/>}

			{hasOverdue &&
				<DayContainer
					key={"overdue_day_container"}
					slim
					modules={overDueModules}
					className={classes.dayContainer}
					showMonthLabel={!hasAnnouncements}
					day={yesterday}
					data-test-id="training-feed-side-menu-overdue-container"
				>
					<OverdueModules
						slim
						modules={overDueModules}
						data-test-id="training-feed-side-menu-overdue-modules"
					/>
				</DayContainer>
			}
			{Object.keys(curriculumState ?? {}).filter(e => e !== "overdue").map((day, i) => {

				if(day === "overdue")
					return <></>

				const dayDate = new Date(parseInt(day))
				dayDate.setHours(0, 0, 0, 0);

				const modules = curriculumState?.[day];

				return <DayContainer
					isLoading={modules === "loading"}
					slim
					showMonthLabel={!hasAnnouncements && !hasOverdue && i === 0}
					className={classes.dayContainer}
					key={day}
					day={dayDate}
					data-test-id={`training-feed-side-menu-day-${day}`}
				>
					<ScheduledDayModules
						slim
						isLoading={modules === "loading"}
						data={(typeof modules === "string" ? [] : modules) ?? []}
						day={next}
					/>
				</DayContainer>
			})}

			<DayContainer
				key={"next_day_container"}
				slim
				className={classes.dayContainer}
				day={nextDayToLoad}
				data-test-id="training-feed-side-menu-load-more-container"
			>
				<Box
					className={classes.placeholderModule}
					data-test-id="training-feed-side-menu-placeholder"
					aria-hidden="true"
				>
					<Box></Box>
					<Box></Box>
				</Box>
				<Box
					className={classes.tapToLoadBtnContainer}
					data-test-id="training-feed-side-menu-button-container"
				>
					<Button
						onClick={handleRequestDay}
						className={classes.tapToLoadBtn}
						data-test-id="training-feed-side-menu-tap-to-load-button"
						aria-label="Load more training modules in side menu"
					>
						{t("training_feed_tap_to_load", "Tap to load")}
					</Button>
				</Box>
			</DayContainer>
		</Box>
	);
});